import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import {
  copySalesLines,
  createBillingDocuments,
  createPartialInvoice,
  fetchProjectBilling,
  revokeBillingDone,
  setBillingDone,
  type CreateDocumentsResult,
  type ProjectBilling,
  type SalesLine,
} from '../api/project-sales.api'
import { POSITION_STATUS_LABELS, alsTag } from '../project-labels'
import { ZustandMarke, geld } from './PositionSalesTab'
import { bestaetige, frage } from '@/lib/dialoge'

const schmal = inlineInput.replace('w-full', '')
const zahl = (n: number) => n.toLocaleString(navigator.language, { maximumFractionDigits: 3 })

const SCHEMA: Record<string, string> = {
  TIME_AND_MATERIAL: 'Regie',
  LUMP_SUM: 'Pauschal',
  UNIT_PRICE: 'Einheitspreise',
  PAYMENT_PLAN: 'Zahlungsplan',
}
const BELEGART: Record<string, string> = {
  OFFER: 'Angebot',
  ORDER_CONFIRMATION: 'Auftragsbestätigung',
  DELIVERY_NOTE: 'Lieferschein',
  INVOICE: 'Rechnung',
  DOWN_PAYMENT_INVOICE: 'Anzahlungsrechnung',
  PARTIAL_INVOICE: 'Teilrechnung',
  FINAL_INVOICE: 'Schlussrechnung',
  CREDIT_NOTE: 'Gutschrift',
  CANCELLATION: 'Storno',
  DUNNING: 'Mahnung',
  CUSTOM: 'Dokument',
}
const GRUND: Record<string, string> = {
  SALES_LINE_NOT_BILLABLE: 'nicht abrechenbar',
  SALES_LINE_ALREADY_DELIVERED: 'schon geliefert',
  SALES_LINE_ALREADY_INVOICED: 'schon abgerechnet',
  SALES_LINE_NOT_DELIVERED: 'noch nicht geliefert (Lieferschein-Pflicht)',
  SALES_LINE_NOT_YET_BILLABLE: 'noch nicht zur Verrechnung frei („Verrechnung ab")',
  PROJECT_NO_CLIENT: 'kein Kunde hinterlegt',
}

/**
 * AP-35 Detailblatt 7 7. — **Abrechnungsliste am Projekt.**
 *
 * Kopf mit Auftragswert, geliefert, abgerechnet, offen und der Freigabe
 * „Abrechnung fertig"; darunter je Position die Verkaufszeilen, dazu „direkt im
 * Beleg" (Belegzeilen mit Positionsbezug, aber ohne Herkunft) und rechts die
 * Belege des Projekts. Aus einer Auswahl entsteht ein Lieferschein oder eine
 * Rechnung **als Entwurf**; erst das Festschreiben ändert die Liste.
 */
export function ProjectBillingListTab({ projectId, onChanged }: { projectId: string; onChanged: () => void }) {
  const { user, hasModule } = useAuthStore()
  const darf = (r: string) => erfuelltRecht(r, user?.permissions)
  const preise = darf('projects.price.view')
  const darfAbrechnen = darf('projects.billing.run')
  const terms = useTerminology()
  const navigate = useNavigate()

  const [b, setB] = useState<ProjectBilling | null>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [erzeugen, setErzeugen] = useState<'DELIVERY_NOTE' | 'INVOICE' | null>(null)
  const [teil, setTeil] = useState(false)

  const laden = useCallback(async () => {
    try {
      setB(await fetchProjectBilling(projectId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Abrechnungsliste konnte nicht geladen werden.'))
    }
  }, [projectId])

  useEffect(() => {
    void laden()
  }, [laden])

  async function tu(aktion: () => Promise<unknown>, erfolg?: string) {
    setFehler(null)
    setHinweis(null)
    try {
      await aktion()
      if (erfolg) setHinweis(erfolg)
      await laden()
      onChanged()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  if (!b) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : <p className="text-sm text-muted-foreground">Lädt …</p>

  const auswahl = [...markiert]
  const umschalten = (id: string) =>
    setMarkiert((m) => {
      const n = new Set(m)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const t = b.totals
  const belegwesen = b.canCreateDocuments && hasModule('CORRESPONDENCE')

  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <div className={`${formCardSm} grid gap-3 sm:grid-cols-5`}>
          <Kachel titel="Auftragswert" wert={preise ? geld(t.value) : t.lines === 1 ? '1 Zeile' : `${t.lines} Zeilen`} unter={preise && t.supplementValue ? `davon Nachträge ${geld(t.supplementValue)}` : undefined} />
          <Kachel titel="geliefert" wert={preise ? geld(t.deliveredValue) : String(t.delivered)} />
          <Kachel titel="abgerechnet" wert={preise ? geld(t.invoicedValue) : String(t.invoiced)} />
          <Kachel titel="offen" wert={preise ? geld(t.openValue) : String(t.open)} />
          <Kachel
            titel={b.scheme === 'LUMP_SUM' ? 'Leistungsstand / abgerechnet' : 'Leistungsstand'}
            wert={b.scheme === 'LUMP_SUM' ? `${b.progressPercent} % / ${b.billedPercent} %` : `${b.progressPercent} %`}
            unter={`${SCHEMA[b.scheme] ?? b.scheme}${b.orderConfirmation ? ` · AB ${b.orderConfirmation.number ?? ''}` : ''}`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {b.billingDone ? (
            <span className="rounded bg-green-100 px-2 py-1 text-green-800 dark:bg-green-900/40 dark:text-green-200">
              Abrechnung fertig seit {alsTag(b.billingDone.at)}
              {darfAbrechnen && (
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={async () => {
                    const grund = await frage('Grund für den Widerruf?')
                    if (grund) void tu(() => revokeBillingDone(projectId, grund), 'Freigabe widerrufen.')
                  }}
                >
                  widerrufen
                </button>
              )}
            </span>
          ) : (
            darfAbrechnen && (
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 hover:bg-muted"
                onClick={async () => {
                  const offen = t.open + t.delivered
                  if (offen && !(await bestaetige(`${offen} Zeilen sind noch nicht abgerechnet. Trotzdem als fertig freigeben?`))) return
                  void tu(() => setBillingDone(projectId), 'Abrechnung als fertig freigegeben.')
                }}
              >
                Abrechnung fertig
              </button>
            )
          )}
          {darfAbrechnen && belegwesen && b.scheme === 'LUMP_SUM' && b.orderConfirmation && (
            <button type="button" className="rounded-md border border-border px-3 py-1.5 hover:bg-muted" onClick={() => setTeil(true)}>
              Teilrechnung …
            </button>
          )}
          {auswahl.length > 0 && (
            <span className="ml-auto flex flex-wrap items-center gap-1">
              <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
              <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => copySalesLines(auswahl), 'In die Zwischenablage kopiert.')}>
                Kopieren
              </button>
              {darfAbrechnen && belegwesen && (
                <>
                  <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => setErzeugen('DELIVERY_NOTE')}>
                    Lieferschein …
                  </button>
                  <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => setErzeugen('INVOICE')}>
                    Rechnung …
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        {!belegwesen && <p className="text-xs text-muted-foreground">Ohne Belegwesen entstehen hier keine Belege — „ausgeliefert am" lässt sich an der Zeile von Hand setzen.</p>}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}

        {b.groups.map((g) => (
          <div key={g.position.id} className="overflow-x-auto rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-sm">
              <Link className="font-medium hover:underline" to={`/projects/${projectId}?tab=positions&position=${g.position.id}`}>
                {terms.position} {g.position.number} · {g.position.name}
              </Link>
              {g.position.type === 'SUPPLEMENT' && <span className="rounded bg-amber-100 px-1.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Nachtrag</span>}
              <span className="text-xs text-muted-foreground">
                {POSITION_STATUS_LABELS[g.position.status]?.label ?? g.position.status} · {SCHEMA[g.position.scheme] ?? g.position.scheme}
              </span>
              {preise && <span className="ml-auto text-sm tabular-nums">{geld(g.totals.value)}</span>}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {g.lines.length === 0 && (
                  <tr>
                    <td className="px-3 py-2 text-muted-foreground">keine Verkaufszeilen</td>
                  </tr>
                )}
                {g.lines.flatMap((z) => [
                  <Zeile key={z.id} z={z} preise={preise} markiert={markiert.has(z.id)} onMark={() => umschalten(z.id)} />,
                  ...z.children.map((k) => <Zeile key={k.id} z={k} preise={preise} kind />),
                ])}
              </tbody>
            </table>
          </div>
        ))}

        {b.direct.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <div className="border-b border-border bg-muted/40 px-3 py-1.5 text-sm font-medium">direkt im Beleg</div>
            <table className="w-full text-sm">
              <tbody>
                {b.direct.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0">
                    <td className="px-2 py-1.5 text-right tabular-nums">{zahl(l.quantity)} {l.unitCode}</td>
                    <td className="px-2 py-1.5 font-semibold">{l.name}</td>
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">{l.positionNumber ? `${terms.position} ${l.positionNumber}` : ''}</td>
                    <td className="px-2 py-1.5 text-xs">
                      <Link className="hover:underline" to={`/correspondence/${l.document.id}`}>
                        {BELEGART[l.document.kind] ?? l.document.kind} {l.document.number}
                      </Link>
                    </td>
                    {preise && <td className="px-2 py-1.5 text-right tabular-nums">{geld(l.lineNet)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={`${formCardSm} h-fit space-y-2`}>
        <h3 className={cardTitle}>Belege</h3>
        {b.documents.length === 0 && <p className="text-sm text-muted-foreground">Noch keine.</p>}
        <ul className="space-y-1 text-sm">
          {b.documents.map((d) => (
            <li key={d.id} className="flex justify-between gap-2 border-t border-border pt-1">
              <Link className="hover:underline" to={`/correspondence/${d.id}`}>
                {BELEGART[d.kind] ?? d.kind} {d.number}
                <span className="block text-xs text-muted-foreground">
                  {alsTag(d.date)} · {d.status === 'DRAFT' ? 'Entwurf' : d.status === 'CANCELLED' ? 'storniert' : 'festgeschrieben'}
                </span>
              </Link>
              {preise && d.netTotal !== undefined && <span className="tabular-nums">{geld(d.netTotal)}</span>}
            </li>
          ))}
        </ul>
      </div>

      {erzeugen && (
        <BelegDialog
          kind={erzeugen}
          lineIds={auswahl}
          preise={preise}
          onClose={() => setErzeugen(null)}
          onDone={(r) => {
            setErzeugen(null)
            setMarkiert(new Set())
            if (r.created.length === 1) navigate(`/correspondence/${r.created[0].documentId}`)
            else void tu(async () => undefined, `${r.created.length} Entwürfe angelegt.`)
          }}
        />
      )}
      {teil && b.orderConfirmation && (
        <TeilrechnungDialog
          projectId={projectId}
          stand={b.progressPercent}
          bereits={b.billedPercent}
          ab={b.orderConfirmation.number}
          onClose={() => setTeil(false)}
          onDone={(id) => navigate(`/correspondence/${id}`)}
        />
      )}
    </div>
  )
}

function Kachel({ titel, wert, unter }: { titel: string; wert: string; unter?: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{titel}</p>
      <p className="text-lg font-semibold tabular-nums">{wert}</p>
      {unter && <p className="text-xs text-muted-foreground">{unter}</p>}
    </div>
  )
}

function Zeile({ z, preise, kind, markiert, onMark }: { z: SalesLine; preise: boolean; kind?: boolean; markiert?: boolean; onMark?: () => void }) {
  return (
    <tr className={`border-b border-border last:border-0 ${z.state === 'NOT_BILLABLE' ? 'opacity-60' : ''}`}>
      <td className="w-8 px-2 py-1.5">{!kind && z.lineType === 'ITEM' && <input type="checkbox" aria-label={`${z.name} markieren`} checked={!!markiert} onChange={onMark} />}</td>
      <td className="w-28 whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{z.lineType === 'TEXT' ? '' : `${zahl(z.quantity)} ${z.unitCode ?? ''}`}</td>
      <td className={`px-2 py-1.5 ${kind ? 'pl-8 text-muted-foreground' : 'font-semibold'}`}>{z.name}</td>
      {preise && <td className="w-24 px-2 py-1.5 text-right tabular-nums">{z.lineType === 'TEXT' || z.children.length ? '' : geld(z.unitPrice)}</td>}
      {preise && <td className="w-16 px-2 py-1.5 text-right tabular-nums">{z.discountPercent ? `${z.discountPercent} %` : ''}</td>}
      {preise && <td className="w-28 px-2 py-1.5 text-right tabular-nums">{z.lineType === 'TEXT' ? '' : geld(z.lineTotal)}</td>}
      <td className="w-44 px-2 py-1.5">{!kind && z.lineType === 'ITEM' && <ZustandMarke z={z} />}</td>
    </tr>
  )
}

/** 5.3 — Vorschau je Kunde, dann Entwürfe anlegen. */
export function BelegDialog({
  kind,
  lineIds,
  preise,
  onClose,
  onDone,
}: {
  kind: 'DELIVERY_NOTE' | 'INVOICE'
  lineIds: string[]
  preise: boolean
  onClose: () => void
  onDone: (r: CreateDocumentsResult) => void
}) {
  const terms = useTerminology()
  const [split, setSplit] = useState<'PER_CUSTOMER' | 'PER_PROJECT'>('PER_CUSTOMER')
  const [adresse, setAdresse] = useState<'NONE' | 'PROJECT_SITE'>('PROJECT_SITE')
  const [vorschau, setVorschau] = useState<CreateDocumentsResult | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    createBillingDocuments({ lineIds, kind, split, deliveryAddress: adresse, dryRun: true })
      .then(setVorschau)
      .catch((e) => setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht laden.')))
  }, [lineIds, kind, split, adresse])

  const anzahl = vorschau?.groups.length ?? 0
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-2xl space-y-3 overflow-auto rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <h2 className="text-lg font-semibold">{kind === 'DELIVERY_NOTE' ? 'Lieferschein erzeugen' : 'Rechnung erzeugen'}</h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <select className={`${schmal} w-56`} value={split} onChange={(e) => setSplit(e.target.value as typeof split)} aria-label="Aufteilung">
            <option value="PER_CUSTOMER">ein Beleg je Kunde</option>
            <option value="PER_PROJECT">je Kunde und {terms.project}</option>
          </select>
          {kind === 'DELIVERY_NOTE' && (
            <select className={`${schmal} w-56`} value={adresse} onChange={(e) => setAdresse(e.target.value as typeof adresse)} aria-label="Lieferadresse">
              <option value="PROJECT_SITE">Lieferung an die Baustelle</option>
              <option value="NONE">an die Anschrift des Kunden</option>
            </select>
          )}
        </div>
        {vorschau?.warning === 'BILLING_MIXED_CUSTOMERS' && <p className="text-sm text-amber-700 dark:text-amber-400">Je Kunde entsteht ein eigener Beleg — {anzahl} Belege.</p>}
        {vorschau?.groups.map((g) => (
          <div key={`${g.clientId}|${g.projectId}`} className="rounded border border-border p-2 text-sm">
            <div className="flex justify-between font-medium">
              <span>
                {g.clientName} <span className="font-normal text-muted-foreground">{g.projects.join(', ')}</span>
              </span>
              {preise && g.total !== undefined && <span className="tabular-nums">{geld(g.total)}</span>}
            </div>
            <ul className="text-xs text-muted-foreground">
              {g.lines.map((l) => (
                <li key={l.lineId}>
                  {l.quantity} {l.unitCode} {l.name} · {l.project}/{l.position}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {vorschau && vorschau.skipped.length > 0 && (
          <div className="text-sm">
            <p className="font-medium">zurückgehalten</p>
            <ul className="text-xs text-muted-foreground">
              {vorschau.skipped.map((s) => (
                <li key={s.lineId}>
                  {s.name}: {GRUND[s.reason] ?? s.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!anzahl}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() =>
              void createBillingDocuments({ lineIds, kind, split, deliveryAddress: adresse })
                .then(onDone)
                .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
            }
          >
            {anzahl === 1 ? '1 Entwurf anlegen' : `${anzahl} Entwürfe anlegen`}
          </button>
        </div>
      </div>
    </div>
  )
}

/** 5.3 — Teilrechnung: der **kumulierte** Stand wird abgefragt, der Beleg bekommt den Zuwachs. */
function TeilrechnungDialog({
  projectId,
  stand,
  bereits,
  ab,
  onClose,
  onDone,
}: {
  projectId: string
  stand: number
  bereits: number
  ab: string | null
  onClose: () => void
  onDone: (documentId: string) => void
}) {
  const [prozent, setProzent] = useState(String(Math.max(stand, bereits)))
  const [fehler, setFehler] = useState<string | null>(null)
  const ziel = Number(prozent.replace(',', '.'))
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <h2 className="text-lg font-semibold">Teilrechnung nach Leistungsstand</h2>
        <p className="text-sm text-muted-foreground">
          Quelle: Auftragsbestätigung {ab}. Leistungsstand {stand} %, bereits abgerechnet {bereits} %.
        </p>
        <label className="block text-sm">
          abgerechnet bis (kumuliert) %
          <input className={`${schmal} ml-2 w-24 text-right`} value={prozent} onChange={(e) => setProzent(e.target.value)} />
        </label>
        {Number.isFinite(ziel) && ziel > bereits && ziel <= 100 ? (
          <p className="text-sm">Diese Rechnung: {Math.round((ziel - bereits) * 100) / 100} % der Auftragsbestätigung.</p>
        ) : (
          <p className="text-sm text-amber-700 dark:text-amber-400">Der Stand muss über {bereits} % und höchstens 100 % liegen.</p>
        )}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!(ziel > bereits && ziel <= 100)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() =>
              void createPartialInvoice({ projectId, percent: ziel })
                .then((r) => r.documentId && onDone(r.documentId))
                .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
            }
          >
            Entwurf anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
